// src/pages/Marketplace/shared/useMarket.ts
import { useState } from 'react';
import type { Market, MarketKind } from './types';

export interface CreateMarketBody {
  name: string;
  kind: MarketKind;
  timezone: string;
  college_name?: string;
  center_lat?: number;
  center_lng?: number;
  radius_km?: number;
  city?: string;
  state?: string;
}

export type UpdateMarketBody = Partial<CreateMarketBody> & { is_active?: boolean };

export function useMarketMutations(apiUrl: string, accessToken: string | null) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const auth = (): HeadersInit => ({
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  });

  const create = async (body: CreateMarketBody): Promise<Market | null> => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${apiUrl}/admin/markets`, {
        method: 'POST', headers: auth(), body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.text()) || `POST failed ${res.status}`);
      return (await res.json()) as Market;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error'); return null;
    } finally { setLoading(false); }
  };

  const update = async (id: string, body: UpdateMarketBody): Promise<Market | null> => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${apiUrl}/admin/markets/${id}`, {
        method: 'PUT', headers: auth(), body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.text()) || `PUT failed ${res.status}`);
      return (await res.json()) as Market;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error'); return null;
    } finally { setLoading(false); }
  };

  const softDelete = async (id: string): Promise<boolean> => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${apiUrl}/admin/markets/${id}`, {
        method: 'DELETE', headers: auth(),
      });
      if (!res.ok) throw new Error((await res.text()) || `DELETE failed ${res.status}`);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error'); return false;
    } finally { setLoading(false); }
  };

  return { loading, error, create, update, softDelete };
}
